import { Link } from "react-router-dom";
import { SiteFooter } from "@/components/SiteFooter";
import { SERVICE_AREA, company, identityLine } from "@/lib/company";

const UPDATED = "2 October 2026";

/**
 * A starting draft, not a substitute for legal review -- same caveat as
 * Privacy.tsx. Covers what both app stores expect a terms link to cover:
 * who can use the service, how payment/wallet works, cancellation, and
 * liability.
 */
export function Terms() {
  return (
    <>
    <div className="mx-auto max-w-2xl px-4 py-12 text-ink-800">
      <Link to="/" className="text-sm font-medium text-brand">
        ← WeShip
      </Link>
      <h1 className="mt-6 text-3xl font-semibold tracking-tight text-ink-900">Terms of Service</h1>
      <p className="mt-1 text-sm text-ink-400">Last updated {UPDATED}</p>

      <div className="prose-sm mt-8 space-y-6 text-[15px] leading-relaxed">
        <p>
          These terms apply to the {company.tradingName} web portal and the {company.tradingName}{" "}
          mobile app. By creating an account or booking a shipment, you agree to them. Questions:{" "}
          <a className="text-brand" href={`mailto:${company.supportEmail}`}>
            {company.supportEmail}
          </a>
          .
        </p>
        <p>
          You are contracting with {identityLine()}, whose details are at the foot of every page.
        </p>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">The service</h2>
          <p className="mt-2">
            {company.tradingName} collects and delivers parcels within {SERVICE_AREA} with its own
            vehicles and drivers. We are the carrier: the price you are quoted, the vehicle that
            arrives, and the person who hands the parcel over are all ours. Where capacity requires
            it we may hand a leg to a subcontracted carrier, and these terms still apply to it.
          </p>
          <p className="mt-2">
            Prices are worked out from the actual road distance between the collection and delivery
            addresses, the service level you choose, and any surcharge applying to either area. Every
            price shown is the price you pay — this business is not VAT registered, so no VAT
            is charged or added.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">Your account</h2>
          <p className="mt-2">
            You're responsible for keeping your login credentials secure and for the accuracy of the
            addresses, parcel details, and declared values you provide — a wrong address or
            understated value can delay a shipment or affect an insurance claim.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">Payment and wallet</h2>
          <p className="mt-2">
            Prepaid accounts pay by topping up a wallet balance; invoiced accounts are billed
            separately by agreement. Top-ups are processed by our payment provider, BobPay, and are
            credited only once the payment is verified — never simply because a payment page
            redirected back. We never see or store your card details.
          </p>
          <p className="mt-2">
            A wallet balance remains your money and does not expire. It is intended to be spent on
            shipments, and we would rather you did, but you can ask for an unspent balance back: see
            the <Link className="text-brand" to="/refunds">refunds and cancellations policy</Link>.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">Prohibited items</h2>
          <p className="mt-2">
            There is a published list of what we can and cannot carry:{" "}
            <Link className="text-brand" to="/prohibited-items">what we can carry</Link>. It is
            generated from the same list the booking form checks, so the two cannot disagree.
          </p>
          <p className="mt-2">
            Sending a prohibited item is a breach of these terms. We may refuse, hold or return a
            parcel that appears to contain one, at your cost, and no claim for its loss or damage is
            payable.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">Cancellations</h2>
          <p className="mt-2">
            You can cancel a shipment before collection and what you paid returns to your wallet in
            full. Once a parcel has been collected it cannot be cancelled — it is in a vehicle and
            the work has been done; a return is a new shipment, quoted as one. The detail, including
            attempted collections and how claims differ from refunds, is in our{" "}
            <Link className="text-brand" to="/refunds">refunds and cancellations policy</Link>.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">Liability</h2>
          <p className="mt-2">
            Our liability for loss or damage in transit is limited to the declared value you give us
            at booking, and to any additional cover you buy at the same time. Declare honestly: an
            understated value is the ceiling on what we can pay out, and an overstated one does not
            raise it.
          </p>
          <p className="mt-2">
            We are not liable for indirect or consequential loss — lost profit, a missed deadline, a
            cancelled event — however the loss arises. Nor are we liable for damage to a parcel that
            was not packed well enough for normal courier handling, or for delay caused by weather,
            road closures, civil unrest, or an address or contact number that turns out to be wrong.
          </p>
          <p className="mt-2">
            Report loss or damage within seven days of the delivery date, or of the date delivery was
            due, with photographs of the packaging and the contents. Nothing in these terms limits
            rights you have under the Consumer Protection Act that cannot lawfully be limited.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">Changes</h2>
          <p className="mt-2">
            We may update these terms from time to time; the date at the top of this page reflects
            the latest version. Continuing to use WeShip after a change means you accept it.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">Privacy</h2>
          <p className="mt-2">
            See our <Link className="text-brand" to="/privacy">Privacy Policy</Link> for how we handle your information.
          </p>
        </section>
      </div>
    </div>
    <SiteFooter />
    </>
  );
}
